<script setup lang="ts">
    import { computed } from 'vue';
    import { useCtUploadViewModel } from '@/views/case-settings/upload/ct/useCtUploadViewModel';
    import AppModalTitle from '@/components/modal/AppModalTitle.vue';
    import AppModalContent from '@/components/modal/AppModalContent.vue';
    import { formatPatientName } from '@/domain/case/personName';

    const upload = useCtUploadViewModel();

    const conflict = computed(() => upload.patientIdentityConflict);
</script>

<template>
    <app-modal
        :model-value="upload.hasMultiplePatientIdentities"
        persistent
        max-width="800"
        width="800"
    >
        <v-card>
            <app-modal-title class="headline patient-inconsistent-identity-container-title" @close="upload.cancel">
                There was an issue processing your upload: DICOM patient details differ between
                files.
            </app-modal-title>

            <v-divider />

            <app-modal-content>
                <v-card-text>
                    We noticed while processing your DICOM files that the patient details contained
                    in some individual files differed from others:
                </v-card-text>

                <v-card-text class="mt-5 small-text">
                    <v-data-table>
                        <template #default>
                            <thead class="custom-grey-light">
                            <tr>
                                <th class="">Files</th>
                                <th class="text-left">Name</th>
                                <th class="text-left">DOB</th>
                                <th class="">Sex</th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr
                                v-for="(group, index) in conflict.groups"
                                :key="`${index}-group`"
                            >
                                <td>
                                    {{ group.fileCount }}
                                    {{ group.fileCount === 1 ? 'file' : 'files' }}
                                </td>
                                <td>{{ formatPatientName(group.patient?.name) }}</td>
                                <td class="custom-grey--text">
                                    {{ group.patient?.birth_date }}
                                </td>
                                <td class="text-capitalize">
                                    {{ group.patient?.sex }}
                                </td>
                            </tr>
                            </tbody>
                        </template>
                        <!--
                            Removes pagination
                            https://stackoverflow.com/questions/76211136/cant-remove-footer-pagination-from-v-data-table-in-vue-3-2
                        -->
                        <template #bottom></template>
                    </v-data-table>
                </v-card-text>

                <v-card-text v-if="conflict.conflictFileNames.length === 1" class="mt-5">
                    The file that was not consistent was
                    <span class="custom-black--text">
                        {{ conflict.conflictFileNames[0] }}
                    </span>
                </v-card-text>
                <v-card-text v-else-if="conflict.conflictFileNames.length" class="mt-5">
                    The {{ conflict.conflictFileNames.length }} files that were not
                    consistent were
                    <span class="custom-black--text"> {{ conflict.formattedConflictList }}. </span>
                </v-card-text>

                <v-card-text>
                    Your DICOM files will not be processed - please review the files with your
                    radiology provider and reupload when you&rsquo;re ready.
                </v-card-text>
            </app-modal-content>

            <app-modal-actions>
                <app-button-secondary @click="upload.cancel">
                    Close
                </app-button-secondary>
            </app-modal-actions>
        </v-card>
    </app-modal>
</template>

<style scoped>
    .patient-inconsistent-identity-container-title {
        white-space: break-spaces;
    }
</style>
